/**
 * SafeGuardSettings — SafeGuard configuration surface.
 *
 * Renders the four rule buckets the reviewer AI uses, the strictness slider
 * that gates whether an email sends or is held as a draft, and the optional
 * spam-scoring layer that runs in parallel.
 *
 * Key state schema (unchanged — the backend reviewer expects these exact keys):
 *   safeGuardSettings = {
 *     enabled, emailDos, emailDonts, targetAudience, veryImportant,
 *     minimumRating (1..10), skipReplyTypes (bool)
 *   }
 *   preSendScorerSettings = {
 *     enabled, strictMode, coldEmailsOnly, samplingRate, budgetCapPerDay
 *   }
 *
 * Layout: full-width two-column grid (controls | rules) so a 13" laptop
 * at full width uses the pane. Advanced + save stay full-bleed below.
 * The rules block puts VOICE input first (SpeakEngine 'safeGuardRules'
 * preset) and collapses each textarea into a preview-snippet by default.
 */

import React, { useState, useEffect, useMemo } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import PreSendScorerSettings from "./PreSendScorerSettings";
// Global SpeakEngine — 'safeGuardRules' preset walks the user through six
// guiding questions and returns fully-written rule text ready to save.
import { useSpeakEngine } from "../speakEngine";
import "./SafeGuardSettings.css";

const SCORER_DEFAULTS = {
  enabled: false,
  strictMode: false,
  coldEmailsOnly: true,
  scoreFollowups: false,
  samplingRate: 100,
  budgetCapPerDay: 50,
};

// The four rule buckets shown in the "Your review rules" section. `key`
// stays in sync with the RTDB schema and the reviewer prompt — only the
// user-facing label, hint, and placeholder are new (friendlier wording
// than "Email DOs / DON'Ts" etc.).
const RULE_FIELDS = [
  {
    key: "emailDos",
    label: "Tone & style — how your emails should sound",
    short: "DOs",
    hint: "Concrete guidance the reviewer uses when judging voice, length, structure and CTAs.",
    placeholder:
      "Example:\n- Professional but human tone\n- Under 150 words, short paragraphs\n- One clear call-to-action\n- Personalised opener referencing the recipient",
    icon: "fa-comment-dots",
  },
  {
    key: "emailDonts",
    label: "Never allow — words, phrases, patterns",
    short: "DON'Ts",
    hint: "Explicit red flags. The more concrete the better — the reviewer treats these as absolute triggers.",
    placeholder:
      "Example:\n- No ALL CAPS or emoji spam\n- No 'URGENT', 'LIMITED TIME', 'ACT NOW'\n- No aggressive sales language\n- No unrealistic promises",
    icon: "fa-ban",
  },
  {
    key: "targetAudience",
    label: "Who you're writing to",
    short: "Audience",
    hint: "The reviewer needs context on your recipients so tone judgements match reality.",
    placeholder:
      "Example:\n- SMB founders and marketing leads, 30–50\n- Decision-makers under time pressure\n- Prefer direct, jargon-free messages\n- German-speaking DACH region",
    icon: "fa-user-group",
  },
  {
    key: "veryImportant",
    label: "Absolute rules — never break these",
    short: "Hard rules",
    hint: "Legal, ethical, and brand-safety rules the AI is not allowed to break under any circumstance.",
    placeholder:
      "Example:\n- Never quote specific ROI %\n- Always GDPR compliant\n- Never mention competitors by name\n- No claims about internal company issues",
    icon: "fa-shield",
  },
];

/* ─────────────────────────── small ui atoms ─────────────────────────── */

function SgSwitch({ checked, onChange, title, hint }) {
  return (
    <label className="sg-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="sg-switch-ui" aria-hidden="true" />
      <span className="sg-switch-text">
        <span className="sg-switch-title">{title}</span>
        {hint && <span className="sg-switch-hint">{hint}</span>}
      </span>
    </label>
  );
}

/* ─────────────────────────── main component ─────────────────────────── */

const SafeGuardSettings = ({ userId }) => {
  const [safeGuardSettings, setSafeGuardSettings] = useState({
    enabled: false,
    emailDos: "",
    emailDonts: "",
    targetAudience: "",
    veryImportant: "",
    minimumRating: 7,
    skipReplyTypes: true,
  });
  const [scorerSettings, setScorerSettings] = useState(SCORER_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  // Which rule bucket is currently expanded for editing. Only one at a time
  // — keeps the surface calm; users tap another to switch focus.
  const [openRule, setOpenRule] = useState(null);
  // Advanced spam-check section is collapsed by default so first-time users
  // aren't confronted with two layers of settings.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { openCapture } = useSpeakEngine();

  useEffect(() => {
    if (!userId) return;

    const safeGuardSettingsRef = ref(database, `/World/${userId}/settings/safeGuardSettings`);
    const unsubscribeListener = onValue(
      safeGuardSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setSafeGuardSettings({
            enabled: data.enabled !== undefined ? data.enabled : false,
            emailDos: data.emailDos || "",
            emailDonts: data.emailDonts || "",
            targetAudience: data.targetAudience || "",
            veryImportant: data.veryImportant || "",
            minimumRating: data.minimumRating || 7,
            skipReplyTypes: data.skipReplyTypes !== undefined ? data.skipReplyTypes : true,
          });
        }
      },
      (error) => {
        console.error("Error loading SafeGuard Settings", error);
      }
    );

    const scorerRef = ref(database, `/World/${userId}/settings/preSendScorerSettings`);
    const unsubScorer = onValue(scorerRef, (snap) => {
      if (snap.exists()) setScorerSettings({ ...SCORER_DEFAULTS, ...snap.val() });
    });

    return () => {
      unsubscribeListener();
      unsubScorer();
    };
  }, [userId]);

  const toggleScorer = async (key) => {
    if (!userId) return;
    const newVal = !scorerSettings[key];
    setScorerSettings((prev) => ({ ...prev, [key]: newVal }));
    try {
      await update(ref(database, `/World/${userId}/settings/preSendScorerSettings`), { [key]: newVal });
    } catch (e) {
      console.error("Scorer toggle save failed:", e);
    }
  };

  const updateScorerValue = async (key, value) => {
    if (!userId) return;
    setScorerSettings((prev) => ({ ...prev, [key]: value }));
    try {
      await update(ref(database, `/World/${userId}/settings/preSendScorerSettings`), { [key]: value });
    } catch (e) {
      console.error("Scorer value save failed:", e);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSafeGuardSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsSaved(false);
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("User not found.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const safeGuardSettingsRef = ref(database, `/World/${userId}/settings/safeGuardSettings`);
      await update(safeGuardSettingsRef, {
        enabled: safeGuardSettings.enabled,
        emailDos: safeGuardSettings.emailDos.trim(),
        emailDonts: safeGuardSettings.emailDonts.trim(),
        targetAudience: safeGuardSettings.targetAudience.trim(),
        veryImportant: safeGuardSettings.veryImportant.trim(),
        minimumRating: safeGuardSettings.minimumRating,
        skipReplyTypes: safeGuardSettings.skipReplyTypes,
      });

      setMessage("Saved. SafeGuard will use these rules on the next send.");
      setIsSaved(true);

      setTimeout(() => {
        setMessage("");
        setIsSaved(false);
      }, 3000);
    } catch (error) {
      console.error("Error saving SafeGuard Settings:", error);
      setMessage("Error saving SafeGuard Settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const rating = Number(safeGuardSettings.minimumRating) || 7;
  // Higher minimum = stricter gate (backend: rating >= minimumRating).
  // Three-band mapping drives both the value pill colour and the plain-
  // English description under the slider.
  const ratingBand = rating <= 3 ? "lenient" : rating <= 7 ? "moderate" : "strict";
  const ratingLabel = {
    lenient: "Relaxed",
    moderate: "Balanced",
    strict: "A lot of mails might not go through",
  }[ratingBand];
  const ratingCopy = {
    lenient:
      "Almost everything sends. Only clearly weak emails are held back as drafts.",
    moderate:
      "Solid emails send automatically. Weak or risky ones are held back for review.",
    strict:
      "Only excellent emails send automatically — most will wait for you as drafts.",
  }[ratingBand];

  // Rules-status summary shown next to the section header.
  const rulesFilled = RULE_FIELDS.filter(
    (f) => (safeGuardSettings[f.key] || "").trim()
  ).length;

  // Opens the SpeakEngine with the safeGuardRules preset. Existing values
  // are forwarded so the model can add detail without wiping user edits.
  const handleSpeakRules = () => {
    openCapture({
      preset: "safeGuardRules",
      context: {
        existingValues: {
          emailDos: safeGuardSettings.emailDos || "",
          emailDonts: safeGuardSettings.emailDonts || "",
          targetAudience: safeGuardSettings.targetAudience || "",
          veryImportant: safeGuardSettings.veryImportant || "",
        },
      },
      onResult: async (values) => {
        // Only set fields the AI actually returned something for. We stage
        // into local state (dirty), and the user still has to hit Save —
        // this preserves the "review before persist" invariant for a
        // security-adjacent surface.
        setSafeGuardSettings((prev) => ({
          ...prev,
          emailDos: (values.emailDos || "").trim() || prev.emailDos,
          emailDonts: (values.emailDonts || "").trim() || prev.emailDonts,
          targetAudience: (values.targetAudience || "").trim() || prev.targetAudience,
          veryImportant: (values.veryImportant || "").trim() || prev.veryImportant,
        }));
        setIsSaved(false);
        return "Rules staged — review and hit Save to activate.";
      },
    });
  };

  // Snippet builder for the collapsed rule accordion header.
  const snippetOf = (text) => {
    if (!text) return "";
    // Take first meaningful line (skip empty / '- ' only) and cap at ~90 chars.
    const firstLine = text
      .split("\n")
      .map((l) => l.replace(/^[-*]\s*/, "").trim())
      .find((l) => l.length > 0);
    if (!firstLine) return "";
    return firstLine.length > 96 ? firstLine.slice(0, 96) + "…" : firstLine;
  };

  const ruleCards = useMemo(() => RULE_FIELDS.map((f) => ({
    ...f,
    value: safeGuardSettings[f.key] || "",
    filled: !!(safeGuardSettings[f.key] || "").trim(),
    snippet: snippetOf(safeGuardSettings[f.key]),
  })), [safeGuardSettings]);

  return (
    <div className="sg">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <header className="sg-page-head">
        <div className="sg-page-head-main">
          <div>
            <h3 className="sg-page-title">SafeGuard</h3>
            <p className="sg-page-sub">
              An AI second-opinion that rates every intro email before it leaves. Weak ones are held back as drafts.
            </p>
          </div>
        </div>
        <span className={`sg-master-badge ${safeGuardSettings.enabled ? "on" : "off"}`}>
          {safeGuardSettings.enabled ? "Active" : "Off"}
        </span>
      </header>

      <div className="sg-cols">
        {/* ── Left: Master switch + strictness ────────────────────── */}
        <section className="sg-section">
        <div className="sg-section-hero">
          <SgSwitch
            checked={safeGuardSettings.enabled}
            onChange={(e) => {
              setSafeGuardSettings((prev) => ({ ...prev, enabled: e.target.checked }));
              setIsSaved(false);
            }}
            title={safeGuardSettings.enabled ? "SafeGuard is active" : "SafeGuard is off"}
            hint={
              safeGuardSettings.enabled
                ? "Every first-touch email is rated 1–10 before it sends. Follow-ups are always skipped."
                : "Emails send with no AI quality check. Recommended: turn on before running any outreach at volume."
            }
          />
        </div>

        {/* Strictness — the single most important knob after on/off */}
        <div className={`sg-strictness ${!safeGuardSettings.enabled ? "is-disabled" : ""}`}>
          <div className="sg-strictness-head">
            <div>
              <span className="sg-strictness-eyebrow">Strictness</span>
              <h4 className="sg-strictness-title">How high should the bar be?</h4>
            </div>
            <span className={`sg-strictness-pill ${ratingBand}`}>
              {rating}/10 · {ratingLabel}
            </span>
          </div>
          <input
            type="range"
            name="minimumRating"
            min="1"
            max="10"
            value={rating}
            onChange={handleChange}
            className="sg-slider"
            disabled={!safeGuardSettings.enabled}
            aria-label="Minimum quality rating"
          />
          <div className="sg-strictness-scale">
            <span>Send almost everything</span>
            <span>Balanced</span>
            <span>Only the best</span>
          </div>
          <p className="sg-strictness-copy">{ratingCopy}</p>
        </div>

        <SgSwitch
          checked={safeGuardSettings.skipReplyTypes}
          onChange={(e) => {
            setSafeGuardSettings((prev) => ({ ...prev, skipReplyTypes: e.target.checked }));
            setIsSaved(false);
          }}
          title={
            safeGuardSettings.skipReplyTypes
              ? "Only check first-touch emails"
              : "Also review replies"
          }
          hint={
            safeGuardSettings.skipReplyTypes
              ? "Replies to existing threads skip the review. Follow-ups always skip. Recommended."
              : "Replies get reviewed too — slower and more expensive, but tighter guardrails."
          }
        />
      </section>

      {/* ── Right: Review rules (with voice-first CTA) ────────────── */}
      <section className="sg-section">
        <header className="sg-section-head">
          <div>
            <span className="sg-section-eyebrow">Your review rules</span>
            <h4 className="sg-section-title">What SafeGuard checks against</h4>
            <p className="sg-section-sub">
              Four short guides the reviewer AI reads before rating an email.
              Cover them once — SafeGuard uses them for every check from now on.
            </p>
          </div>
          <span className="sg-section-badge">
            {rulesFilled}/4 filled
          </span>
        </header>

        {/* Voice-first hero — one big affordance, matches the design language
            of the other SpeakEngine callsites (Vault, Ultimate Setup). */}
        <button
          type="button"
          className="sg-speak-hero"
          onClick={handleSpeakRules}
        >
          <span className="sg-speak-hero-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <path d="M12 19v3" />
            </svg>
          </span>
          <span className="sg-speak-hero-text">
            <strong>
              Speak your rules — SafeGuard sets itself up
              <span className="sg-speak-hero-flag">New · AI voice</span>
            </strong>
            <span>We ask you six short questions. You talk for 2–3 min, we fill the four rule sets for you.</span>
          </span>
          <span className="sg-speak-hero-cta">
            Start
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 12h14" /><path d="m13 5 7 7-7 7" />
            </svg>
          </span>
        </button>

        <div className="sg-or-write">
          <span className="sg-or-line" aria-hidden />
          <span className="sg-or-label">or edit by hand</span>
          <span className="sg-or-line" aria-hidden />
        </div>

        {/* Accordion of the 4 rule buckets. One expanded at a time keeps the
            page calm; snippets show what's inside without opening. */}
        <ul className="sg-rules-list">
          {ruleCards.map((f) => {
            const isOpen = openRule === f.key;
            return (
              <li key={f.key} className={`sg-rule ${isOpen ? "is-open" : ""} ${f.filled ? "is-filled" : "is-empty"}`}>
                <button
                  type="button"
                  className="sg-rule-head"
                  onClick={() => setOpenRule(isOpen ? null : f.key)}
                  aria-expanded={isOpen}
                >
                  <span className="sg-rule-head-left">
                    <span className="sg-rule-icon" aria-hidden>
                      <i className={`fa ${f.icon}`} />
                    </span>
                    <span className="sg-rule-labels">
                      <span className="sg-rule-title">
                        {f.label}
                        {f.filled && <span className="sg-rule-dot" title="Filled" />}
                      </span>
                      {f.filled ? (
                        <span className="sg-rule-snippet">{f.snippet}</span>
                      ) : (
                        <span className="sg-rule-snippet sg-rule-snippet--empty">
                          Empty — tap to add
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="sg-rule-chevron" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div className="sg-rule-body">
                    <p className="sg-rule-hint">{f.hint}</p>
                    <textarea
                      id={`sg-${f.key}`}
                      name={f.key}
                      value={f.value}
                      onChange={handleChange}
                      className="sg-textarea"
                      placeholder={f.placeholder}
                      rows={5}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>
      </div>

      {/* ── Section 3: Advanced (spam / deliverability check) ───────── */}
      <section className={`sg-section sg-section--advanced ${showAdvanced ? "is-open" : ""}`}>
        <button
          type="button"
          className="sg-advanced-toggle"
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
        >
          <span className="sg-advanced-toggle-left">
            <span className="sg-advanced-icon" aria-hidden>
              <i className="fa fa-envelope-circle-check" />
            </span>
            <span>
              <span className="sg-section-eyebrow">Advanced</span>
              <span className="sg-advanced-title">
                Spam filter check (deliverability)
              </span>
              <span className="sg-advanced-sub">
                Runs alongside SafeGuard: scans outgoing mail for spam triggers so it lands in inbox, not junk.
              </span>
            </span>
          </span>
          <span className="sg-advanced-right">
            <span className={`sg-badge ${scorerSettings.enabled ? "on" : "off"}`}>
              {scorerSettings.enabled ? "On" : "Off"}
            </span>
            <span className={`sg-advanced-chev ${showAdvanced ? "is-open" : ""}`} aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </span>
        </button>

        {showAdvanced && (
          <div className="sg-advanced-body">
            <SgSwitch
              checked={scorerSettings.enabled}
              onChange={() => toggleScorer("enabled")}
              title={scorerSettings.enabled ? "Spam scan is on" : "Spam scan is off"}
              hint={
                scorerSettings.enabled
                  ? "Every checked email is scored for spam risk before it goes out."
                  : "No spam analysis — you rely on the mailbox's built-in filter only."
              }
            />

            {scorerSettings.enabled && (
              <div className="sg-nested">
                <SgSwitch
                  checked={scorerSettings.strictMode}
                  onChange={() => toggleScorer("strictMode")}
                  title={
                    scorerSettings.strictMode
                      ? "Block risky emails (not just warn)"
                      : "Warn only — never block"
                  }
                  hint={
                    scorerSettings.strictMode
                      ? "High-risk emails are actually stopped and saved as drafts. Safer, but you'll have more drafts to review."
                      : "Scores are logged in your activity, but nothing is held back."
                  }
                />
                <SgSwitch
                  checked={scorerSettings.coldEmailsOnly}
                  onChange={() => toggleScorer("coldEmailsOnly")}
                  title={
                    scorerSettings.coldEmailsOnly
                      ? "Cold outreach only"
                      : "Every outgoing email"
                  }
                  hint={
                    scorerSettings.coldEmailsOnly
                      ? "First-contact emails only. Replies and follow-ups skip. Cheaper."
                      : "Runs on every email — including replies and follow-ups."
                  }
                />

                <div className="sg-metrics">
                  <div className="sg-metric">
                    <label className="sg-field-label" htmlFor="sg-sampling">
                      How many % to check
                    </label>
                    <div className="sg-metric-row">
                      <input
                        id="sg-sampling"
                        type="range"
                        min="10"
                        max="100"
                        step="10"
                        value={scorerSettings.samplingRate}
                        onChange={(e) => updateScorerValue("samplingRate", Number(e.target.value))}
                        className="sg-slider"
                      />
                      <span className="sg-metric-val">{scorerSettings.samplingRate}%</span>
                    </div>
                    <p className="sg-field-hint">Sample rate for scoring. Lower = fewer API calls, lower cost.</p>
                  </div>
                  <div className="sg-metric">
                    <label className="sg-field-label" htmlFor="sg-budget">
                      Max checks per day
                    </label>
                    <div className="sg-metric-row">
                      <input
                        id="sg-budget"
                        type="number"
                        min="5"
                        max="500"
                        value={scorerSettings.budgetCapPerDay}
                        onChange={(e) =>
                          updateScorerValue(
                            "budgetCapPerDay",
                            Math.max(5, Math.min(500, Number(e.target.value)))
                          )
                        }
                        className="sg-number"
                      />
                      <span className="sg-metric-val muted">/ day</span>
                    </div>
                    <p className="sg-field-hint">Hard cap to prevent surprise bills (5–500).</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Save bar ────────────────────────────────────────────────── */}
      <div className="sg-actions">
        <button
          type="button"
          className={`sg-save ${isSaved ? "saved" : ""}`}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving…" : isSaved ? "Saved" : "Save settings"}
        </button>
        {message && (
          <p className={`sg-message ${/^Error|not found/i.test(message) ? "error" : "success"}`}>
            {message}
          </p>
        )}
      </div>

      {scorerSettings.enabled && showAdvanced && (
        <section className="sg-activity">
          <PreSendScorerSettings userId={userId} />
        </section>
      )}
    </div>
  );
};

export default SafeGuardSettings;
